import { useAccount, usePublicClient, useWalletClient } from "wagmi"
import { getDrift } from "@/lib/utils/coins"
import {
  DOPPLER_V4_ADDRESSES,
  dopplerAbi,
  ReadQuoter,
  universalRouterAbi,
} from "doppler-v4-sdk"
import { Address, maxUint256 } from "viem"
import { CommandBuilder, V4ActionBuilder, V4ActionType } from "doppler-router"
import { useRef } from "react"
import { CHAIN_ID } from "./constants"

export function useCoinsSupport(poolAddress: string) {
  const poolKey = useRef<{
    currency0: `0x${string}`
    currency1: `0x${string}`
    fee: number
    tickSpacing: number
    hooks: `0x${string}`
  }>()
  const account = useAccount()
  const { data: walletClient } = useWalletClient(account)
  const publicClient = usePublicClient({ chainId: CHAIN_ID })

  async function getQuote(amount: bigint) {
    const drift = getDrift(CHAIN_ID, walletClient)
    const quoter = new ReadQuoter(
      DOPPLER_V4_ADDRESSES[CHAIN_ID].v4Quoter,
      // @ts-ignore
      drift
    )
    const contractPoolKey = await publicClient?.readContract({
      address: poolAddress as Address,
      abi: dopplerAbi,
      functionName: "poolKey",
    })

    if (!contractPoolKey) return

    poolKey.current = {
      currency0: contractPoolKey[0],
      currency1: contractPoolKey[1],
      fee: contractPoolKey[2],
      tickSpacing: contractPoolKey[3],
      hooks: contractPoolKey[4],
    }

    const quoteConfig = {
      poolKey: poolKey.current,
      zeroForOne: true,
      exactAmount: amount,
      hookData: "0x" as `0x${string}`,
    }
    const quote = await quoter.quoteExactInputV4(quoteConfig)
    return quote
  }

  async function buyCoins(amount: bigint) {
    if (!poolKey.current || !walletClient)
      throw new Error("Missing values for swap")

    const actionBuilder = new V4ActionBuilder()
    const [actions, params] = actionBuilder
      .addSwapExactInSingle(poolKey.current, true, amount, 0n, "0x")
      .addAction(V4ActionType.SETTLE_ALL, [
        poolKey.current.currency0,
        maxUint256,
      ])
      .addAction(V4ActionType.TAKE_ALL, [poolKey.current.currency1, 0])
      .build()
    const [commands, inputs] = new CommandBuilder()
      .addV4Swap(actions, params)
      .build()

    const txHash = await walletClient.writeContract({
      address: DOPPLER_V4_ADDRESSES[CHAIN_ID].universalRouter,
      abi: universalRouterAbi,
      functionName: "execute",
      args: [commands, inputs],
      value: amount,
    })

    return txHash
  }

  async function supportPost(amount: bigint) {
    if (!account.address || !publicClient)
      throw new Error("Missing values for support")

    if (account.chainId !== CHAIN_ID) throw new Error("Wrong chain")

    const quote = await getQuote(amount)

    if (!quote) throw new Error("Missing quote for coins")

    const balance = await publicClient.getBalance({ address: account.address })
    const total = amount + (BigInt(2) * quote.gasEstimate) / BigInt(18)

    if (balance < total)
      throw new Error("Not enough balance for this transaction")

    const txHash = await buyCoins(amount)

    return { txHash, quotedAmount: quote.amountOut / BigInt(1e18) }
  }

  return { supportPost }
}
